import axiosInstance from '../axiosInstance';
import { getManagerRoutePath } from '../managerRouteUtil';

/**
 * Recruitment service — talks to /api/carehome-manager/recruitment.
 *
 * A "candidate" is an applicant who has not yet become staff. They move through
 * four steps: the pre-interview form, the interview, the personal details form,
 * and the transfer into the Staff Directory — which is the only point at which
 * a user ID and PIN are created.
 *
 * Two candidate-facing forms, each with its own link:
 *   sendPreInterviewLink  step 1, before an interview is booked
 *   sendFormLink          step 3, after a successful interview
 *
 * Most write calls return the full updated candidate, so callers can drop the
 * response straight into state rather than refetching.
 */
const recruitmentService = {
  // Candidate list + KPI tiles. params: { search, stage, view }
  // `view` is 'active' (the worklist) or 'history' (closed applications); the
  // response carries the count of BOTH so the tabs never show a stale number.
  list: async (params = {}) => {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.get(`/${routePath}/recruitment`, { params });
    return response.data;
  },

  // The document / check / onboarding-task definitions the backend scores against.
  definitions: async () => {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.get(`/${routePath}/recruitment/definitions`);
    return response.data;
  },

  // Full candidate record: their submission, progress and the next action.
  get: async (candidateId) => {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.get(`/${routePath}/recruitment/${candidateId}`);
    return response.data;
  },

  // Add a candidate. { name, email, phone, role_id, source, interview_date, notes }
  create: async (payload) => {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.post(`/${routePath}/recruitment`, payload);
    return response.data;
  },

  // Update basics and/or the manager-owned detail fields. { name, email, phone, role_id, userDetails }
  update: async (candidateId, payload) => {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.put(`/${routePath}/recruitment/${candidateId}`, payload);
    return response.data;
  },

  // Step 1 — email the pre-interview form. Re-sending is also how the form is
  // reopened after the candidate has submitted it. { expiry_days, message }
  sendPreInterviewLink: async (candidateId, payload = {}) => {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.post(
      `/${routePath}/recruitment/${candidateId}/pre-interview-link`,
      payload,
    );
    return response.data;
  },

  // Step 2 — email the interview invitation or the recorded outcome, built
  // from the SAVED interview record. type: 'invitation' | 'outcome'.
  sendInterviewEmail: async (candidateId, type, message = '') => {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.post(
      `/${routePath}/recruitment/${candidateId}/interview-email`,
      { type, message },
    );
    return response.data;
  },

  // Step 3 — email the personal details form. { expiry_days, message }
  sendFormLink: async (candidateId, payload = {}) => {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.post(`/${routePath}/recruitment/${candidateId}/invite`, payload);
    return response.data;
  },

  // Email a set-PIN link — the same page the forgotten-PIN flow uses. Also
  // assigns the automatic user ID if the candidate is still on a placeholder.
  sendPinLink: async (candidateId) => {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.post(`/${routePath}/recruitment/${candidateId}/pin-link`, {});
    return response.data;
  },

  // Record the manager's verdict on one compliance check, and the supporting
  // detail behind it. This is a decision a person makes; receiving a document
  // never sets it. payload: { status, note, date, expiry, fields }
  setManagerCheck: async (candidateId, checkId, payload = {}) => {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.patch(
      `/${routePath}/recruitment/${candidateId}/checks`,
      { check_id: checkId, ...payload },
    );
    return response.data;
  },

  // Attach a document on the candidate's behalf — for when they emailed it or
  // handed it over rather than using the form.
  //
  // `groupId` is a document GROUP (cv | rtw | ni | dbs), not a storage id: the
  // server decides where the file lands, so Right to Work gains another row
  // while the others replace their single one. Attaching resets the matching
  // check back to pending, because new evidence nobody has read is not a pass.
  uploadDocument: async (candidateId, groupId, file, docType) => {
    const routePath = getManagerRoutePath();
    const formData = new FormData();
    formData.append('file', file);
    if (docType) formData.append('doc_type', docType);
    const response = await axiosInstance.post(
      `/${routePath}/recruitment/${candidateId}/documents/${groupId}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },

  // Remove one attached file. `storageId` is a storage id (cv, rtw-2), not a
  // group — deleting the second Right to Work document leaves the others.
  removeDocument: async (candidateId, storageId) => {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.delete(
      `/${routePath}/recruitment/${candidateId}/documents/${storageId}`,
    );
    return response.data;
  },

  // Step 3 — ask this candidate for one more document, by name. It appears on
  // their personal details form as a slot to fill, and they cannot submit
  // without it. { label, note }
  requestExtraDocument: async (candidateId, payload) => {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.post(
      `/${routePath}/recruitment/${candidateId}/extra-documents`,
      payload,
    );
    return response.data;
  },

  // Withdraw a request, and any file already sent against it.
  removeExtraDocument: async (candidateId, storageId) => {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.delete(
      `/${routePath}/recruitment/${candidateId}/extra-documents/${storageId}`,
    );
    return response.data;
  },

  // ── Recruitment links (organisation level) ────────────────────────────────
  // The reusable DBS / Right to Work provider URLs, saved once and then sent to
  // individual candidates from the Checks tab.

  getLinks: async () => {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.get(`/${routePath}/recruitment/links`);
    return response.data;
  },

  // linkId: 'dbs' | 'rtw'. Send an empty url to clear the link.
  saveLink: async (linkId, payload) => {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.put(`/${routePath}/recruitment/links/${linkId}`, payload);
    return response.data;
  },

  // Email the saved links to one candidate. { link_ids, subject, message }
  sendCheckLinks: async (candidateId, payload) => {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.post(`/${routePath}/recruitment/${candidateId}/check-links`, payload);
    return response.data;
  },

  // Email ONE referee their reference form. Per referee, not all at once: a
  // manager is usually chasing the one that has not come back.
  requestReference: async (candidateId, referenceId) => {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.post(
      `/${routePath}/recruitment/${candidateId}/references/${referenceId}/request`,
      {},
    );
    return response.data;
  },

  // Final step — set the roster and access, and move the candidate into Staff
  // Management as a "Setup pending" record. Same payload shape the Add Staff
  // form posts, so the two creation paths produce the same record.
  // { menu_ids, is_carer, app_access, group_id, week_pattern, contracted_hours,
  //   pattern_mode, shift_name }
  convertToStaff: async (candidateId, payload) => {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.post(`/${routePath}/recruitment/${candidateId}/convert`, payload);
    return response.data;
  },

  // Pause an application, or take it off hold.
  // stage: 'On hold' | 'Resume'. The four pipeline steps themselves are derived
  // from progress and cannot be set from here; ending an application goes
  // through archive() below, which records the reason and the date.
  setStage: async (candidateId, stage, reason) => {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.patch(`/${routePath}/recruitment/${candidateId}/stage`, { stage, reason });
    return response.data;
  },

  // End an application and move it to History. Nothing is deleted — the reason
  // becomes the candidate's stage so the record says why it ended.
  // reason: 'Unsuccessful' | 'Withdrawn' | 'Not proceeding'
  archive: async (candidateId, { reason, date, note }) => {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.post(`/${routePath}/recruitment/${candidateId}/archive`, { reason, date, note });
    return response.data;
  },

  // Bring an archived application back. The reason is required by the API.
  reopen: async (candidateId, reason) => {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.post(`/${routePath}/recruitment/${candidateId}/reopen`, { reason });
    return response.data;
  },

  remove: async (candidateId) => {
    const routePath = getManagerRoutePath();
    const response = await axiosInstance.delete(`/${routePath}/recruitment/${candidateId}`);
    return response.data;
  },
};

export default recruitmentService;
